// Чистата логика на справката „Изписвания": филтрите в колоните (Вид ·
// Причина · Статус · Склад · Въвел · Бруто от–до), търсенето и сортирането.
// Сегментите Всички / Изписване / Бракуване са пряк път към филтъра „Вид" —
// едно и също състояние. Броячите (и в сегментите) са фасетни: по периода след
// търсенето и ОСТАНАЛИТЕ филтри (`components/filters/columnFilterModel.ts`).
import type { Writeoff } from "../../../../api/inventory";
import { isWriteoffAnnulled } from "../../writeoff/components/writeoffMeta";
import {
  EMPTY_RANGE,
  type FilterSpecs,
  type NumberRange,
} from "../../components/filters/columnFilterModel";
import { writeoffReasonKey } from "../../../../api/inventoryWriteoffReasons";

export type WriteoffKindKey = "all" | "writeoff" | "scrap";

export type WriteoffFilters = {
  kinds: string[]; // writeoff | scrap
  reasons: string[];
  statuses: string[]; // active | annulled
  warehouses: string[];
  users: string[];
  gross: NumberRange;
};

export const EMPTY_WRITEOFF_FILTERS: WriteoffFilters = {
  kinds: [],
  reasons: [],
  statuses: [],
  warehouses: [],
  users: [],
  gross: EMPTY_RANGE,
};

export type WriteoffFilterKey = keyof WriteoffFilters;

export type WriteoffSortKey = "date" | "doc" | "warehouse" | "sum";
export type WriteoffSort = { key: WriteoffSortKey; dir: "asc" | "desc" };

export const kindOf = (writeoff: Writeoff) => (writeoff.kind === "scrap" ? "scrap" : "writeoff");
// Причините, различни само по малки/главни букви и интервали, са ЕДНА (ключът
// е общ с подсказките на листа и с BE `reason_group_key`)
export const reasonOf = (writeoff: Writeoff) => writeoffReasonKey(writeoff.reason) || "—";
export const warehouseOf = (writeoff: Writeoff) => writeoff.warehouseName || "—";
export const userOf = (writeoff: Writeoff) => writeoff.createdByName || "—";
export const statusOf = (writeoff: Writeoff) =>
  isWriteoffAnnulled(writeoff) ? "annulled" : "active";

// Сегментът е изгледът на филтъра „Вид": точно един вид → той, иначе „Всички"
export const kindSegmentOf = (kinds: string[]): WriteoffKindKey =>
  kinds.length === 1 && (kinds[0] === "writeoff" || kinds[0] === "scrap") ? kinds[0] : "all";

export type WriteoffFilterLabels = {
  titles: Record<WriteoffFilterKey, string>;
  kindLabel: (value: string) => string;
  reasonLabel: (value: string) => string;
  statusLabel: (value: string) => string;
  formatMoney: (value: number) => string;
};

export const writeoffFilterSpecs = ({
  titles,
  kindLabel,
  reasonLabel,
  statusLabel,
  formatMoney,
}: WriteoffFilterLabels): FilterSpecs<Writeoff, WriteoffFilters> => ({
  kinds: {
    kind: "list",
    title: titles.kinds,
    valuesOf: (writeoff) => [kindOf(writeoff)],
    labelOf: kindLabel,
    order: ["writeoff", "scrap"],
  },
  reasons: { kind: "list", title: titles.reasons, valuesOf: (writeoff) => [reasonOf(writeoff)], labelOf: reasonLabel },
  statuses: {
    kind: "list",
    title: titles.statuses,
    valuesOf: (writeoff) => [statusOf(writeoff)],
    labelOf: statusLabel,
    order: ["active", "annulled"],
  },
  warehouses: { kind: "list", title: titles.warehouses, valuesOf: (writeoff) => [warehouseOf(writeoff)] },
  users: { kind: "list", title: titles.users, valuesOf: (writeoff) => [userOf(writeoff)] },
  gross: {
    kind: "range",
    title: titles.gross,
    valueOf: (writeoff) => writeoff.totalValueWithVat || 0,
    format: formatMoney,
  },
});

const matchesQuery = (writeoff: Writeoff, words: string[], reasonLabel: (reason: string) => string) => {
  const haystack = [
    `№${writeoff.id}`,
    writeoff.reason ? reasonLabel(writeoff.reason) : "",
    writeoff.comment ?? "",
    userOf(writeoff),
    warehouseOf(writeoff),
    ...writeoff.lines.map((line) => `${line.productName} ${line.recipeName ?? ""}`),
  ]
    .join(" ")
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
};

// Търсачката над таблицата — базата, върху която работят филтрите в колоните
export const searchWriteoffs = (
  writeoffs: Writeoff[],
  query: string,
  reasonLabel: (reason: string) => string,
) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return words.length
    ? writeoffs.filter((writeoff) => matchesQuery(writeoff, words, reasonLabel))
    : writeoffs;
};

export const sortWriteoffs = (writeoffs: Writeoff[], sort: WriteoffSort) => {
  const sign = sort.dir === "asc" ? 1 : -1;
  const compare = (a: Writeoff, b: Writeoff) => {
    switch (sort.key) {
      case "date":
        return a.date.localeCompare(b.date);
      case "doc":
        return a.id - b.id;
      case "warehouse":
        return warehouseOf(a).localeCompare(warehouseOf(b), "bg");
      case "sum":
        return (a.totalValueWithVat || 0) - (b.totalValueWithVat || 0);
    }
  };
  return [...writeoffs].sort((a, b) => compare(a, b) * sign || b.id - a.id);
};
